'use client'

import React, { useState, useCallback, useMemo, useEffect } from 'react'
// next-intl Link auto-prefixes the active locale onto locale-less internal
// hrefs (CMS `path` values), so taxonomy nav links resolve 200 instead of 301.
import { Link } from '@/i18n/routing'
import { usePathname } from 'next/navigation'
import { Icon } from '@/ui/atoms/Icon'

export interface NestedNavItem {
  id: string
  icon?: string
  label: string
  path?: string
  children?: NestedNavItem[]
}

const styles = {
  item: {
    base: 'flex items-center justify-between w-full transition-all ps-[8px] text-label-XS-regular h-[30px]',
    root: 'pe-0',
    child: 'pe-0',
    active: 'text-text-accent border-l-2 border-blue-500 -ms-[2px] text-label-XS-semibold',
    activeChild: 'text-text-accent',
    inactive: 'text-text-subtle',
    parent: 'text-text-subtle',
    parentActive: 'text-text-base',
    parentWithActiveChild: 'text-text-base',
    parentInactive: 'text-text-subtle',
    withActiveBorder: 'border-l-2 border-blue-500 pl-1',
    rootItem: 'text-label-XS-regular text-text-base !ps-0',
    rootItemActive: 'text-label-S-semibold text-text-accent !ps-0',
    rootItemWithActiveChild: 'text-label-XS-semibold text-text-base !ps-0',
  },
  childList: {
    base: 'border-l-2 border-gray-200 mt-1',
    first: 'ms-[6px]',
    nested: 'ms-[14px]',
  },
  activeChildList: {
    base: 'border-l-2 border-neutral-95 mt-1',
    first: 'ms-[6px]',
    nested: 'ms-[14px]',
  },
  icon: 'flex-shrink-0 mr-[2px]',
  arrow: {
    base: 'size-4 max-size-4 min-size-4 transition-all cursor-pointer bg-neutral-95 rounded-sm hover:!bg-neutral-90',
    up: 'transform -rotate-90',
    down: 'transform rotate-90',
    active: '!bg-bg-brand-blue text-text-accent',
    inactive: 'bg-neutral-95',
    open: 'bg-neutral-90',
  },
  link: 'flex-grow pe-1 h-[24px] flex items-center',
  linkHover:
    'ps-[4px] hover:text-text-base canhover:hover:translate-x-0.5 transition-all duration-150 ease-in-out',
  linkWrapper: 'group flex-grow rounded-sm ps-[4px]',
} as const

export interface NestedNavProps {
  data: NestedNavItem[]
  renderItem?: (item: NestedNavItem, isOpen: boolean) => React.ReactNode
  className?: string
  pathOverride?: string
  title?: string
}

const RecursiveNavItem: React.FC<{
  item: NestedNavItem
  renderItem?: (item: NestedNavItem, isOpen: boolean) => React.ReactNode
  childActive?: boolean
  pathOverride?: string
  showIcon?: boolean
  isRootItem?: boolean
  isFirstLevel?: boolean
}> = ({
  item,
  renderItem,
  childActive = false,
  pathOverride,
  showIcon = false,
  isRootItem = false,
  isFirstLevel = true,
}) => {
  // Default to expanded so users see all stages and sub-processes at a glance.
  // Toggling via the arrow still works to collapse individual branches.
  const [open, setOpen] = useState(true)
  const routerPathname = usePathname()?.replace(/^\/[^/]+/, '') || ''
  const pathname = pathOverride || routerPathname

  const hasActiveDescendant = useCallback(
    (node?: NestedNavItem): boolean => {
      if (!node) return false
      if (node.path && pathname === node.path) return true

      // Only return true if node has a direct child that is active
      if (node.children) {
        for (const child of node.children) {
          if (hasActiveDescendant(child)) {
            return true
          }
        }
      }

      return false
    },
    [pathname],
  )

  const isCurrentPage = item.path === pathname

  const isActive = useMemo(
    () =>
      isCurrentPage ||
      (childActive && !isCurrentPage) ||
      (!isCurrentPage && hasActiveDescendant(item)),
    [childActive, hasActiveDescendant, item, isCurrentPage],
  )

  const passChildActive = isActive && !isCurrentPage

  useEffect(() => {
    if (isActive) {
      setOpen(true)
    }
  }, [isActive])

  const handleArrowClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setOpen(!open)
    },
    [open],
  )

  return (
    <li>
      <div
        className={`${styles.item.base} ${isRootItem ? styles.item.root : styles.item.child} ${
          isRootItem
            ? isCurrentPage
              ? styles.item.rootItemActive
              : hasActiveDescendant(item) && !isCurrentPage
                ? styles.item.rootItemWithActiveChild
                : styles.item.rootItem
            : isCurrentPage
              ? styles.item.active
              : item.children
                ? hasActiveDescendant(item) && !isCurrentPage
                  ? styles.item.parentWithActiveChild
                  : isActive
                    ? styles.item.parentActive
                    : styles.item.parent
                : styles.item.inactive
        }`}
      >
        {item.icon && showIcon && (
          <span className={styles.icon}>
            <Icon name={item.icon} />
          </span>
        )}

        <span className={styles.link}>
          {item.path ? (
            <Link
              href={item.path}
              className={`block w-full px-1 leading-[1.1] ${styles.linkHover}`}
            >
              {renderItem?.(item, open) || item.label}
            </Link>
          ) : (
            <span
              className={`px-1 ${styles.linkHover} cursor-pointer`}
              onClick={(e) => {
                e.stopPropagation()
                setOpen(!open)
              }}
            >
              {renderItem?.(item, open) || item.label}
            </span>
          )}
        </span>

        {item.children && item.children.length > 0 && (
          <button
            onClick={handleArrowClick}
            className={`${styles.arrow.base}
              ${open ? styles.arrow.up : styles.arrow.down}
              ${
                hasActiveDescendant(item)
                  ? styles.arrow.active
                  : open
                    ? styles.arrow.open
                    : styles.arrow.inactive
              }`}
          >
            <Icon name={'arrow-right-s-line'} />
          </button>
        )}
      </div>
      {open && Array.isArray(item.children) && item.children.length > 0 && (
        <ol
          className={`${
            hasActiveDescendant(item) ? styles.activeChildList.base : styles.childList.base
          } ${
            isFirstLevel
              ? isRootItem
                ? styles.childList.first
                : styles.childList.nested
              : styles.childList.nested
          }`}
        >
          {item.children.map((child) => {
            // Check if this child is on the active path
            const childIsOnActivePath = hasActiveDescendant(child)

            return (
              <RecursiveNavItem
                key={child.id}
                item={child}
                renderItem={renderItem}
                // Only pass childActive to the specific child that's on the active path
                childActive={passChildActive && childIsOnActivePath}
                pathOverride={pathOverride}
                isRootItem={false} // Child items are never root items
                isFirstLevel={false} // Deeper nested items are never first level
              />
            )
          })}
        </ol>
      )}
    </li>
  )
}

// Test data covering all possible scenarios
export const testData: NestedNavItem[] = [
  {
    id: '1',
    label: '1 Collection & Transportation',
    icon: 'heart-fill',
    children: [
      {
        id: '1.1',
        label: '1.1 Collection Centers',
        path: '/collection-centers',
      },
      {
        id: '1.2',
        label: '1.2 Transportation Methods',
        path: '/transportation-methods',
      },
    ],
  },
  {
    id: '2',
    label: '2 Incoming Inspection & Storage',
    icon: 'heart-fill',
    children: [
      {
        id: '2.1',
        label: '2.1 Initial Inspection',
        children: [
          {
            id: '2.1.1',
            label: '2.1.1 Visual Inspection',
            path: '/visual-inspection',
          },
          {
            id: '2.1.2',
            label: '2.1.2 Safety Check',
            path: '/safety-check',
          },
        ],
      },
      {
        id: '2.2',
        label: '2.2 Storage Requirements',
        path: '/storage-requirements',
      },
    ],
  },
  {
    id: '3',
    label: '3 Deactivation & Dismantling',
    icon: 'heart-fill',
    children: [
      {
        id: '3.1',
        label: '3.1 Electrical Discharging',
        path: '/electrical-discharging',
      },
      {
        id: '3.2',
        label: '3.2 Deactivating',
        path: '/deactivating',
      },
      {
        id: '3.3',
        label: '3.3 Dismantling',
        path: '/dismantling',
      },
    ],
  },
  {
    id: '4',
    label: '4 Material Separation',
    icon: 'heart-fill',
    path: '/material-separation', // Parent with path
  },
  {
    id: '5',
    label: '5 Material Recovery',
    icon: 'heart-fill',
    children: [
      {
        id: '5.1',
        label: 'Pyrometallurgy',
        children: [
          {
            id: '5.1.1',
            label: '5.1.1 High Temperature',
            path: '/high-temperature',
          },
          {
            id: '5.1.2',
            label: '5.1.2 Smelting',
            path: '/smelting',
          },
        ],
      },
      {
        id: '5.2',
        label: 'Hydrometallurgy',
        children: [
          {
            id: '5.2.1',
            label: '5.2.1 Leaching',
            path: '/leaching',
          },
          {
            id: '5.2.2',
            label: '5.2.2 Precipitation',
            path: '/precipitation',
          },
        ],
      },
    ],
  },
]

// Example usage in stories or tests:
/*
<NestedNav
  data={testData}
  className="max-w-xs"
/>
*/

export const NestedNav: React.FC<NestedNavProps> = ({
  data,
  renderItem,
  className = '',
  pathOverride,
}) => {
  return (
    <div
      className={`transition-all duration-500 ease-in-out pt-4 mt-1 max-h-[2000px] border-t border-neutral-95`}
    >
      <ol className={`w-full overflow-y-auto scrollbar-hide ${className}`}>
        {data.map((item, index) => (
          <React.Fragment key={item.id}>
            <RecursiveNavItem
              item={item}
              renderItem={renderItem}
              pathOverride={pathOverride}
              showIcon={true}
              isRootItem={true}
              isFirstLevel={true}
            />
            {index < data.length - 1 && <hr className={'my-4 border-neutral-95'} />}
          </React.Fragment>
        ))}
      </ol>
    </div>
  )
}

export default NestedNav
